import { useEffect, useMemo, useState } from "react";
import { clsx } from "clsx";
import { Plus, RotateCcw, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { DatePicker } from "@/components/ui/DatePicker";
import { Modal } from "@/components/ui/Modal";
import { SearchableSelect } from "@/components/ui/SearchableSelect";
import { Spinner } from "@/components/ui/Spinner";
import { MILESTONE_STATES, milestoneMeta } from "@/features/power_projects/components/ui";
import type { MilestoneState } from "@/features/power_projects/types";
import { useHandOver, useHandOverForm, usePriceList } from "../hooks";
import type { HandOverReportCycleInput, HandOverTileInput, LeadOrderLineInput, LedSide, LedTile } from "../types";
import { WEEKDAYS } from "./ReportsTab";
import { INPUT_SM, today } from "./common";

interface TileRow {
  tempId: string;
  id?: string;
  label: string;
  originalLabel: string;
  state: string;
  originalState: string;
  dueDate: string;
  originalDue: string;
  isCustom: boolean;
  side: LedSide | null;
  deleted: boolean;
  isNew: boolean;
}

interface LineRow {
  tempId: string;
  /** Wiersz jest ALBO cennikowy (wybór pozycji), ALBO ręczny (nazwa + jm + stawka).
   *  Jedno i drugie w jednej linijce nie ma sensu — o typie decyduje przycisk. */
  manual: boolean;
  typeId: string;
  name: string;
  unit: string;
  rate: string;
  quantity: string;
}

const NA_STATE = "na";

function rowsFromTiles(tiles: LedTile[], fallbackDue: string): TileRow[] {
  return tiles.map((t) => ({
    tempId: t.id,
    id: t.id,
    label: t.label,
    originalLabel: t.label,
    state: t.state,
    originalState: t.state,
    dueDate: t.due_date ?? fallbackDue,
    originalDue: t.due_date ?? "",
    isCustom: t.is_custom,
    side: t.side,
    deleted: false,
    isNew: false,
  }));
}

function toInputs(rows: TileRow[]): HandOverTileInput[] {
  const out: HandOverTileInput[] = [];
  for (const r of rows) {
    if (r.isNew) {
      if (r.deleted || !r.label.trim()) continue;
      out.push({ label: r.label.trim(), due_date: r.dueDate || null, side: r.side, state: r.state });
      continue;
    }
    if (r.deleted) {
      out.push({ id: r.id, delete: true });
      continue;
    }
    const item: HandOverTileInput = { id: r.id, side: r.side };
    if (r.label.trim() !== r.originalLabel) item.label = r.label.trim();
    if (r.dueDate !== r.originalDue) item.due_date = r.dueDate || null;
    if (r.state !== r.originalState) item.state = r.state;
    out.push(item);
  }
  return out;
}

/** Wiersze gotowe do wysłania — puste (nieuzupełnione) po prostu pomijamy. */
function toLineItems(rows: LineRow[]): LeadOrderLineInput[] {
  const out: LeadOrderLineInput[] = [];
  for (const r of rows) {
    const qty = r.quantity.trim().replace(",", ".");
    if (!r.manual) {
      if (r.typeId) out.push({ line_item_type: r.typeId, quantity: qty });
      continue;
    }
    if (!r.name.trim()) continue;
    out.push({
      custom_name: r.name.trim(),
      custom_unit: r.unit.trim() || "usługa",
      unit_rate: r.rate.trim().replace(",", "."),
      quantity: qty,
    });
  }
  return out;
}

function money(value: number): string {
  return `${value.toLocaleString("pl-PL", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} zł`;
}

function rowValue(r: LineRow): number {
  const qty = Number(r.quantity.replace(",", "."));
  const rate = Number(r.rate.replace(",", "."));
  return Number.isFinite(qty) && Number.isFinite(rate) ? qty * rate : 0;
}

/** Jedna siatka dla nagłówka i wierszy — kolumny mają się zgadzać co do piksela. */
const LINE_GRID = "grid grid-cols-[minmax(12rem,1fr)_5rem_7rem_5rem_7.5rem_1.5rem] items-start gap-2";

/** Kafle: nazwa · stan · termin · kto robi · kosz — jeden wiersz, bez zawijania. */
const TILE_GRID = "grid grid-cols-[minmax(12rem,1fr)_10.5rem_10rem_12rem_1.5rem] items-center gap-2";

const SEG_BASE =
  "px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider border transition-colors";

function SideSwitch({
  value,
  onChange,
  missing,
}: {
  value: LedSide | null;
  onChange: (v: LedSide | null) => void;
  missing?: boolean;
}) {
  return (
    <span className={clsx("inline-flex", missing && "ring-1 ring-[var(--color-primary)]")}>
      <button
        type="button"
        onClick={() => onChange(value === "gk" ? null : "gk")}
        className={clsx(
          SEG_BASE,
          value === "gk"
            ? "border-[var(--color-primary)] bg-[var(--color-primary)] text-white"
            : "border-[var(--color-border)] text-[var(--color-text-secondary)]",
        )}
      >
        GK ELEKTIM
      </button>
      <button
        type="button"
        onClick={() => onChange(value === "contractor" ? null : "contractor")}
        className={clsx(
          SEG_BASE,
          "-ml-px",
          value === "contractor"
            ? "border-[#1f3f8f] bg-[#1f3f8f] text-white"
            : "border-[var(--color-border)] text-[var(--color-text-secondary)]",
        )}
      >
        Podwykonawca
      </button>
    </span>
  );
}

interface Props {
  connectionId: string;
  onClose: () => void;
  onDone: (leadershipId: string) => void;
}

/** „Przekaż podwykonawcy" — dyrektor techniczny / CEO. Jedno kliknięcie tworzy
 *  zamówienie „Prowadzenie zlecenia" (termin + pozycje ustawiane tutaj) i przełącza
 *  projekt w widok nadzorczy. */
export function HandOverDialog({ connectionId, onClose, onDone }: Props) {
  const { data: form, isLoading, isError } = useHandOverForm(connectionId, true);
  const mutation = useHandOver(connectionId);
  const [contractor, setContractor] = useState("");
  const [caretaker, setCaretaker] = useState("");
  const [deadline, setDeadline] = useState("");
  const [lines, setLines] = useState<LineRow[]>([]);
  const [wantsCycle, setWantsCycle] = useState(false);
  const [cycle, setCycle] = useState<HandOverReportCycleInput>({
    recipient: "client",
    frequency: "weekly",
    starts_on: today(),
    weekday: 4,
    day_of_month: 5,
    label: "",
  });
  const [note, setNote] = useState("");
  const [rows, setRows] = useState<TileRow[]>([]);
  const [dueTouched, setDueTouched] = useState<Record<string, true>>({});
  const [error, setError] = useState<string | null>(null);
  const { data: priceList, isLoading: priceLoading } = usePriceList(contractor || undefined);

  useEffect(() => {
    if (!form) return;
    const due = form.default_deadline ?? "";
    setDeadline(due);
    setRows(rowsFromTiles(form.tiles, due));
    // Kafel, który ma już swój termin z projektu, nie podąża za terminem zamówienia.
    setDueTouched(Object.fromEntries(form.tiles.filter((t) => t.due_date).map((t) => [t.id, true as const])));
    setCaretaker(form.default_caretaker);
  }, [form]);

  // Termin zamówienia jest domyślnym terminem każdego kafla — dopóki ktoś nie ruszy
  // terminu konkretnego kafla ręcznie.
  useEffect(() => {
    setRows((prev) => prev.map((r) => (dueTouched[r.tempId] ? r : { ...r, dueDate: deadline })));
  }, [deadline, dueTouched]);

  const contractorOptions = useMemo(
    () => (form?.contractors ?? []).map((c) => ({ value: c.id, label: c.name })),
    [form],
  );

  const priceOptions = useMemo(
    () =>
      (priceList ?? []).map((p) => ({
        value: p.id,
        label: `${p.name} — ${p.unit}${p.base_rate != null ? ` @ ${money(Number(p.base_rate))}` : ""}`,
      })),
    [priceList],
  );

  const update = (tempId: string, patch: Partial<TileRow>) =>
    setRows((prev) => prev.map((r) => (r.tempId === tempId ? { ...r, ...patch } : r)));

  const setTileDue = (tempId: string, value: string) => {
    setDueTouched((prev) => ({ ...prev, [tempId]: true }));
    update(tempId, { dueDate: value });
  };

  const assignAll = (side: LedSide) =>
    setRows((prev) => prev.map((r) => (r.deleted || r.state === NA_STATE ? r : { ...r, side })));

  const addRow = () => {
    const tempId = `new-${Date.now()}-${rows.length}`;
    setRows((prev) => [
      ...prev,
      {
        tempId,
        label: "",
        originalLabel: "",
        state: "not_started",
        originalState: "not_started",
        dueDate: deadline,
        originalDue: "",
        isCustom: true,
        side: "contractor",
        deleted: false,
        isNew: true,
      },
    ]);
  };

  const addLine = (manual: boolean) =>
    setLines((prev) => [
      ...prev,
      {
        tempId: `line-${Date.now()}-${prev.length}`,
        manual,
        typeId: "",
        name: "",
        unit: "",
        rate: "",
        quantity: "1",
      },
    ]);

  const updateLine = (tempId: string, patch: Partial<LineRow>) =>
    setLines((prev) => prev.map((l) => (l.tempId === tempId ? { ...l, ...patch } : l)));

  const pickCatalogItem = (tempId: string, typeId: string) => {
    const item = (priceList ?? []).find((p) => p.id === typeId);
    updateLine(tempId, {
      typeId,
      name: item?.name ?? "",
      unit: item?.unit ?? "",
      rate: item?.base_rate ?? "",
    });
  };

  const unassigned = rows.filter((r) => !r.deleted && r.state !== NA_STATE && !r.side);
  const withoutDue = rows.filter((r) => !r.deleted && r.state !== NA_STATE && !r.dueDate);
  const orderValue = lines.reduce((sum, l) => sum + rowValue(l), 0);

  const submit = async () => {
    setError(null);
    if (!contractor) return setError("Wybierz firmę prowadzącą.");
    if (!deadline) return setError("Podaj termin zamówienia — bez niego nie przekazujemy projektu.");
    const lineItems = toLineItems(lines);
    if (lineItems.length === 0)
      return setError("Dodaj co najmniej jedną pozycję zamówienia — z cennika umowy ramowej albo ręczną.");
    if (lines.some((l) => !l.manual && !l.typeId))
      return setError("Wybierz pozycję z cennika w każdym wierszu cennikowym albo usuń pusty wiersz.");
    const noName = lines.find((l) => l.manual && !l.name.trim());
    if (noName) return setError("Pozycja ręczna musi mieć nazwę.");
    const broken = lines.find(
      (l) => l.manual && (!l.rate.trim() || !(Number(l.rate.replace(",", ".")) > 0)),
    );
    if (broken) return setError(`Pozycja ręczna „${broken.name.trim()}” musi mieć stawkę.`);
    if (lines.some((l) => !(Number(l.quantity.replace(",", ".")) > 0)))
      return setError("Każda pozycja zamówienia musi mieć ilość większą od zera.");
    if (unassigned.length > 0)
      return setError(
        `Każdy kafel musi być przydzielony — GK ELEKTIM albo podwykonawca. Bez strony: ${unassigned
          .map((r) => r.label || "bez nazwy")
          .join(", ")}.`,
      );
    if (withoutDue.length > 0)
      return setError(
        `Każdy kafel musi mieć termin. Bez terminu: ${withoutDue.map((r) => r.label || "bez nazwy").join(", ")}.`,
      );
    try {
      const summary = await mutation.mutateAsync({
        contractor,
        caretaker: caretaker || null,
        deadline,
        line_items: lineItems,
        report_cycle: wantsCycle ? cycle : null,
        note,
        tiles: toInputs(rows),
      });
      onDone(summary.id);
    } catch (e: unknown) {
      const payload = (e as { response?: { data?: { errors?: Record<string, unknown> } } })?.response?.data?.errors;
      const flat =
        typeof payload?.detail === "string"
          ? payload.detail
          : payload
            ? Object.values(payload).flat().join(" ")
            : null;
      setError(flat ?? "Nie udało się przekazać projektu.");
    }
  };

  const contractorName = contractorOptions.find((c) => c.value === contractor)?.label;

  return (
    <Modal open onClose={onClose} title="Przekaż projekt podwykonawcy" size="xl">
      {isLoading && (
        <div className="flex justify-center p-8">
          <Spinner />
        </div>
      )}
      {isError && <p className="p-4 text-sm text-[var(--color-primary)]">Nie udało się pobrać danych do przekazania.</p>}
      {form && form.already_led && (
        <p className="p-4 text-sm text-[var(--color-primary)]">Ten projekt jest już prowadzony przez podwykonawcę.</p>
      )}
      {form && !form.already_led && (
        <div className="flex flex-col gap-4 p-4 text-sm">
          <SearchableSelect
            label="Firma prowadząca"
            options={contractorOptions}
            value={contractor}
            onChange={(e) => setContractor((e.target as HTMLSelectElement).value)}
            placeholder="Wybierz firmę z kartoteki"
          />

          <label className="block">
            <span className="section-label">Opiekun po stronie ELEKTIM</span>
            <select
              className="input-base mt-1 w-full"
              value={caretaker}
              onChange={(e) => setCaretaker(e.target.value)}
            >
              {form.caretakers.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </label>

          {/* Zamówienie „Prowadzenie zlecenia" — termin i pozycje ustala się TUTAJ,
              bo tylko tutaj to zamówienie powstaje. */}
          <div className="border border-[var(--color-border)] bg-[var(--color-bg-elevated)] px-3 py-2">
            <span className="section-label">Zamówienie „Prowadzenie zlecenia”</span>
            <p className="mt-1 text-[12px] text-[var(--color-text-muted)]">
              {contractorName ? `${contractorName} · ` : ""}projekt ZE {form.project_name} · dalej w module Zamówień.
            </p>

            <div className="mt-2 flex flex-wrap items-end gap-3">
              <label className="block">
                <span className="section-label">Termin zamówienia</span>
                <DatePicker
                  value={deadline}
                  clearable={false}
                  className="mt-1 w-[11rem]"
                  onChange={(v) => setDeadline(v || "")}
                />
              </label>
              <p className="pb-1.5 text-[12px] text-[var(--color-text-muted)]">
                Domyślny termin kafli.
              </p>
            </div>

            <div className="mt-3">
              <div className="mb-1 flex flex-wrap items-center justify-between gap-2">
                <span className="section-label">Pozycje zamówienia</span>
                <span className="flex gap-2">
                  <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    disabled={!contractor || priceOptions.length === 0}
                    onClick={() => addLine(false)}
                  >
                    <Plus size={14} /> Z cennika
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    disabled={!contractor}
                    onClick={() => addLine(true)}
                  >
                    <Plus size={14} /> Ręczna
                  </Button>
                </span>
              </div>
              {!contractor && (
                <p className="text-[12px] text-[var(--color-text-muted)]">Najpierw wybierz firmę.</p>
              )}
              {contractor && priceLoading && (
                <p className="text-[12px] text-[var(--color-text-muted)]">Wczytuję cennik…</p>
              )}
              {contractor && !priceLoading && priceOptions.length === 0 && (
                <p className="text-[12px] text-[var(--color-text-muted)]">
                  Brak cennika w umowie ramowej — dodaj pozycję ręczną.
                </p>
              )}
              <div className="overflow-x-auto border border-[var(--color-border)]">
                <div className="min-w-[40rem]">
                  <div className={clsx(LINE_GRID, "border-b border-[var(--color-border)] px-2 py-1")}>
                    <span className="section-label">Pozycja</span>
                    <span className="section-label">Jm</span>
                    <span className="section-label text-right">Stawka</span>
                    <span className="section-label text-right">Ilość</span>
                    <span className="section-label text-right">Wartość</span>
                    <span />
                  </div>
                  <ul className="divide-y divide-[var(--color-border-muted)]">
                    {lines.map((l) => (
                      <li key={l.tempId} className={clsx(LINE_GRID, "px-2 py-1.5")}>
                        {l.manual ? (
                          <>
                            <input
                              className={INPUT_SM}
                              value={l.name}
                              placeholder="nazwa pozycji (spoza cennika)"
                              onChange={(e) => updateLine(l.tempId, { name: e.target.value })}
                            />
                            <input
                              className={INPUT_SM}
                              value={l.unit}
                              placeholder="szt."
                              onChange={(e) => updateLine(l.tempId, { unit: e.target.value })}
                            />
                            <input
                              className={clsx(INPUT_SM, "text-right")}
                              value={l.rate}
                              inputMode="decimal"
                              placeholder="0,00"
                              onChange={(e) => updateLine(l.tempId, { rate: e.target.value })}
                            />
                          </>
                        ) : (
                          <>
                            <SearchableSelect
                              options={priceOptions}
                              value={l.typeId}
                              placeholder="— wybierz pozycję z cennika —"
                              onChange={(e) => pickCatalogItem(l.tempId, (e.target as HTMLSelectElement).value)}
                            />
                            <span className="self-center text-[12px] text-[var(--color-text-secondary)]">
                              {l.unit || "—"}
                            </span>
                            <span className="self-center text-right font-mono text-[12px] tabular-nums">
                              {l.rate ? money(Number(l.rate)) : "—"}
                            </span>
                          </>
                        )}
                        <input
                          className={clsx(INPUT_SM, "text-right")}
                          value={l.quantity}
                          inputMode="decimal"
                          aria-label="ilość"
                          onChange={(e) => updateLine(l.tempId, { quantity: e.target.value })}
                        />
                        <span className="self-center text-right font-mono text-[12px] tabular-nums">
                          {money(rowValue(l))}
                        </span>
                        <button
                          type="button"
                          className="self-center text-[var(--color-text-muted)] hover:text-[var(--color-primary)]"
                          title="Usuń pozycję"
                          onClick={() => setLines((prev) => prev.filter((x) => x.tempId !== l.tempId))}
                        >
                          <Trash2 size={14} />
                        </button>
                      </li>
                    ))}
                    {lines.length === 0 && (
                      <li className="px-3 py-3 text-[12px] text-[var(--color-text-muted)]">
                        Brak pozycji — zamówienie bez nich nie powstanie.
                      </li>
                    )}
                  </ul>
                </div>
              </div>
              {lines.length > 0 && (
                <p className="mt-1 text-right font-mono text-[12px] tabular-nums">Razem: {money(orderValue)}</p>
              )}
            </div>
          </div>

          <div>
            <div className="mb-1 flex flex-wrap items-center justify-between gap-2">
              <span className="section-label">Kafle projektu — po czyjej stronie</span>
              <span className="flex items-center gap-2">
                <span className="text-[11px] text-[var(--color-text-muted)]">wszystkie:</span>
                <Button type="button" variant="ghost" size="sm" onClick={() => assignAll("gk")}>
                  GK ELEKTIM
                </Button>
                <Button type="button" variant="ghost" size="sm" onClick={() => assignAll("contractor")}>
                  Podwykonawca
                </Button>
                <Button type="button" variant="secondary" size="sm" onClick={addRow}>
                  <Plus size={14} /> Dodaj kafel
                </Button>
              </span>
            </div>
            <div className="overflow-x-auto border border-[var(--color-border)]">
              <div className="min-w-[46rem]">
                <div className={clsx(TILE_GRID, "border-b border-[var(--color-border)] px-2 py-1")}>
                  <span className="section-label">Kafel</span>
                  <span className="section-label">Stan</span>
                  <span className="section-label">Termin</span>
                  <span className="section-label">Kto robi</span>
                  <span />
                </div>
                <ul className="divide-y divide-[var(--color-border-muted)]">
                  {rows.map((r) => {
                    const needsSide = !r.deleted && r.state !== NA_STATE && !r.side;
                    return (
                      <li
                        key={r.tempId}
                        className={clsx(
                          TILE_GRID,
                          "px-2 py-1.5",
                          r.deleted && "opacity-50",
                          needsSide && "bg-[var(--color-primary)]/5",
                        )}
                      >
                        <input
                          className="input-base !py-1 text-[13px]"
                          value={r.label}
                          disabled={r.deleted}
                          placeholder={r.isNew ? "nazwa nowego kafla" : undefined}
                          onChange={(e) => update(r.tempId, { label: e.target.value })}
                        />
                        {/* Stan da się poprawić od razu — kafle wchodzą do prowadzenia w tym
                            stanie, w jakim naprawdę są. Backend zapisuje to przez MilestoneService,
                            więc tablica kamieni i dziennik widzą tę samą zmianę. */}
                        <span className="flex items-center gap-1.5">
                          <i
                            className="h-2 w-2 shrink-0"
                            style={{ background: milestoneMeta(r.state as MilestoneState).accent }}
                            aria-hidden
                          />
                          <select
                            className="input-base !py-1 min-w-0 flex-1 text-[12px]"
                            value={r.state}
                            disabled={r.deleted}
                            aria-label={`Stan kafla ${r.label || "nowego"}`}
                            onChange={(e) => update(r.tempId, { state: e.target.value })}
                          >
                            {MILESTONE_STATES.map((st) => (
                              <option key={st} value={st}>
                                {milestoneMeta(st).label}
                              </option>
                            ))}
                          </select>
                        </span>
                        <DatePicker
                          value={r.dueDate}
                          disabled={r.deleted}
                          placeholder="termin"
                          className="!py-1 text-[13px]"
                          aria-label={`Termin kafla ${r.label || "nowego"}`}
                          onChange={(v) => setTileDue(r.tempId, v || "")}
                        />
                        <SideSwitch
                          value={r.side}
                          missing={needsSide}
                          onChange={(side) => update(r.tempId, { side })}
                        />
                        <button
                          type="button"
                          className="text-[var(--color-text-muted)] hover:text-[var(--color-primary)]"
                          title={
                            r.deleted
                              ? "Przywróć"
                              : r.isCustom
                                ? "Usuń kafel"
                                : "Usuń (wbudowany kafel: zostanie oznaczony jako nie dotyczy)"
                          }
                          onClick={() => update(r.tempId, { deleted: !r.deleted })}
                        >
                          {r.deleted ? <RotateCcw size={14} /> : <Trash2 size={14} />}
                        </button>
                      </li>
                    );
                  })}
                  {rows.length === 0 && (
                    <li className="px-3 py-3 text-[var(--color-text-muted)]">
                      Projekt nie ma jeszcze kafli — dodaj pierwszy.
                    </li>
                  )}
                </ul>
              </div>
            </div>
            <p className="mt-1 text-[12px] text-[var(--color-text-muted)]">
              Każdy kafel: gospodarz i termin (poza „nie dotyczy”). Później zmienisz w zakładce „Etapy”.
              {unassigned.length > 0 && (
                <strong className="ml-1 text-[var(--color-primary)]">Bez gospodarza: {unassigned.length}.</strong>
              )}
            </p>
          </div>

          {/* Harmonogram raportów ustawiany od razu — inaczej „raporty co tydzień"
              zostaje ustaleniem mailowym, którego nikt nie pilnuje. */}
          <fieldset>
            <legend className="section-label">Raporty okresowe</legend>
            <label className="mt-1 flex items-center gap-2">
              <input type="checkbox" checked={wantsCycle} onChange={(e) => setWantsCycle(e.target.checked)} />
              <span>Ustaw harmonogram raportów od razu</span>
            </label>
            {wantsCycle && (
              <div className="mt-2 grid grid-cols-1 gap-3 sm:grid-cols-4">
                <label className="block">
                  <span className="section-label">Odbiorca</span>
                  <select
                    className="input-base mt-1 w-full"
                    value={cycle.recipient}
                    onChange={(e) => setCycle((c) => ({ ...c, recipient: e.target.value as HandOverReportCycleInput["recipient"] }))}
                  >
                    <option value="client">Zamawiający (przez nas)</option>
                    <option value="gk">GK ELEKTIM</option>
                  </select>
                </label>
                <label className="block">
                  <span className="section-label">Częstotliwość</span>
                  <select
                    className="input-base mt-1 w-full"
                    value={cycle.frequency}
                    onChange={(e) => setCycle((c) => ({ ...c, frequency: e.target.value as HandOverReportCycleInput["frequency"] }))}
                  >
                    <option value="weekly">tygodniowo</option>
                    <option value="monthly">miesięcznie</option>
                  </select>
                </label>
                {cycle.frequency === "weekly" ? (
                  <label className="block">
                    <span className="section-label">Dzień tygodnia</span>
                    <select
                      className="input-base mt-1 w-full"
                      value={cycle.weekday}
                      onChange={(e) => setCycle((c) => ({ ...c, weekday: Number(e.target.value) }))}
                    >
                      {WEEKDAYS.map((w, i) => (
                        <option key={w} value={i}>{w}</option>
                      ))}
                    </select>
                  </label>
                ) : (
                  <label className="block">
                    <span className="section-label">Dzień miesiąca</span>
                    <input
                      type="number"
                      min={1}
                      max={28}
                      className="input-base mt-1 w-full"
                      value={cycle.day_of_month}
                      onChange={(e) => setCycle((c) => ({ ...c, day_of_month: Number(e.target.value) }))}
                    />
                  </label>
                )}
                <label className="block">
                  <span className="section-label">Od kiedy</span>
                  <DatePicker
                    value={cycle.starts_on}
                    clearable={false}
                    className="mt-1"
                    onChange={(v) => setCycle((c) => ({ ...c, starts_on: v || today() }))}
                  />
                </label>
              </div>
            )}
          </fieldset>

          <label className="block">
            <span className="section-label">Uwaga do dziennika (opcjonalnie)</span>
            <textarea className="input-base mt-1 w-full" rows={2} value={note} onChange={(e) => setNote(e.target.value)} />
          </label>

          <p className="text-[12px] text-[var(--color-text-muted)]">
            Podwykonawca widzi dziennik od dnia przekazania (kreska w osi czasu) oraz nasze pisma i odpowiedzi
            z rejestru. Nigdy: KPI, zadania, notatki wewnętrzne, kwoty i zamówienia innych firm.
          </p>

          <p className="text-[12px] text-[var(--color-text-secondary)]">
            Warsztat projektu zostaje wyłączony dla wszystkich, automaty (doradca, etapy, SLA, WPW, Wody Polskie) też.
            Wpis trafi do dziennika.
          </p>

          {error && <p className="text-sm text-[var(--color-primary)]">{error}</p>}

          <div className="flex justify-end gap-2 border-t border-[var(--color-border)] pt-3">
            <Button type="button" variant="secondary" onClick={onClose}>
              Anuluj
            </Button>
            <Button type="button" onClick={submit} disabled={mutation.isPending}>
              Przekaż i utwórz zamówienie
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
}
